import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { CustomerModel } from 'src/app/models/customer-model';
import { WebservicesService } from '../webservices/webservices.service';
import { filter, map } from 'rxjs/operators';
import type { ILoginResponsePayload } from 'src/app/interfaces/authentication.interface';
import { Platform } from '@ionic/angular';
import { ConfigService } from '../config/config.service';
import { API_LIB } from 'src/library/api.lib';

export enum eMfaStatuses {
  required = 'required',
  passed = 'passed',
  unknown = 'unknown'
}

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>
export type TUserStatus = PartialBy<ILoginResponsePayload, 'lastIPAddress' | 'lastLogoutReason' | 'deviceId'> | null


@Injectable({
  providedIn: 'root',
})
export class UserSessionService {

  private customerSubject = new BehaviorSubject<CustomerModel | null>(null);
  private customerIdSubject = new BehaviorSubject<number | string | null>(null);
  private sessionTokenSubject = new BehaviorSubject<string | null>(null);
  private _mfaStatus$ = new BehaviorSubject<eMfaStatuses | eMfaStatuses.unknown>(null);

  public userStatus: Subject<any> = new Subject();

  public sessionState$ = combineLatest([
    this.sessionTokenSubject,
    this.customerIdSubject
  ]).pipe(
    map(([sessionToken, customerId]) => ({ sessionToken, customerId }))
  );

  public customer: CustomerModel;
  public customerId: any;
  public customerIPAddress: any;
  public mfaEnabled: boolean = false;
  public sessionToken: string;

  public readonly sessionToken$ = this.sessionTokenSubject.asObservable();

  constructor(
    private config: ConfigService,
    private platform: Platform,
    private webservices: WebservicesService,
  ) {
    const allowPreviousDisconnect = this.config?.isEnabled('FEATURE_ALLOW_PREVIOUS_DISCONNECTION', true);
    
    let storedToken;
    let storedCustomer;
    if(this.platform.is('capacitor') && allowPreviousDisconnect) {
      storedToken = sessionStorage.getItem('userSession.sessionToken');
      storedCustomer = sessionStorage.getItem('userSession.customer');
    } else {
      storedToken = localStorage.getItem('userSession.sessionToken');  
      storedCustomer = localStorage.getItem('userSession.customer');
    }    

    if (storedToken) {
      this.setSessionToken(storedToken);
    }
    if (storedCustomer) {
      const parsed = JSON.parse(storedCustomer);
      this.setCustomer(parsed || null);
      this.setCustomerId(parsed?.customer_id || parsed?.customerId || null);
    }
  }

  public setSessionToken(sessionToken: string | null | undefined): void {
    const normalizedToken = sessionToken ?? null;

    if (this.sessionToken === normalizedToken) {
      return;
    }

    this.sessionToken = normalizedToken;

    if (normalizedToken) {
      localStorage.setItem('userSession.sessionToken', normalizedToken);
      sessionStorage.setItem('userSession.sessionToken', sessionToken);
    } else {
      localStorage.removeItem('userSession.sessionToken');
      sessionStorage.removeItem('userSession.sessionToken');
    }

    this.sessionTokenSubject.next(normalizedToken);
  }

  public setCustomerIPAddress(IPAddress: any) {
    localStorage.setItem('lastIPAddress', IPAddress);
    this.customerIPAddress = IPAddress;
  }

  public getSessionToken() {
    if (this.sessionToken) {
      return this.sessionToken;
    }
    const allowPreviousDisconnect = this.config?.isEnabled('FEATURE_ALLOW_PREVIOUS_DISCONNECTION', true);
    const sessionToken = (this.platform.is('capacitor') && allowPreviousDisconnect) ?
      sessionStorage.getItem('userSession.sessionToken') :
      localStorage.getItem('userSession.sessionToken');
    if (sessionToken) {
      this.setSessionToken(sessionToken);
    }

    return this.sessionToken;
  }

  public getCustomerIPAddress() {
    return this.customerIPAddress;
  }

  refreshCustomer() {
    const response = new Subject();
    if (this.sessionToken) {
      this.webservices
        .get('authentication/get-account', { session_token: this.sessionToken })
        .subscribe((resp: PlatformResponse<any>) => {
          if (resp.result && resp.payload && resp.payload.customer) {
            this.setCustomer(resp.payload.customer);
            response.next(resp.payload.customer);
            response.complete();
          }
        });
    } else {
      response.next(null);
      response.complete();
    }
    return response;
  }

  updateAccount(customer_id: string, newProps: any): Promise<void> {
    return new Promise((resolve, error) => {
      newProps.session_token = this.sessionToken;
      newProps.customer_id = customer_id;
      //
      console.warn('user-session.ts: updateAccount() setting');
      this.webservices
        .post(API_LIB.AUTHENTICATION_UPDATE_ACCOUNT, newProps)
        .subscribe({
          next: () => {
            resolve();
          },
          error: (err: any) => {
            error(err);
          }
        });
    });
  }

  acceptTerms(customerId) {
    const obj: any = {
      customer_id: customerId,
      session_token: this.sessionToken,
    };

    return this.webservices.post('customer/accept-terms', obj);
  }

  acceptPrivacyPolicy(customerId) {
    const obj: any = {
      customer_id: customerId,
      session_token: this.sessionToken,
    };

    return this.webservices.post('customer/accept-privacy-policy', obj);
  }

  acceptHouseRules(customerId) {
    const obj: any = {
      customer_id: customerId,
      session_token: this.sessionToken,
    };

    return this.webservices.post('customer/accept-house-rules', obj);
  }

  public async clear() {
    localStorage.removeItem('userSession.sessionToken');
    localStorage.removeItem('userSession.customer');
    sessionStorage.removeItem('userSession.sessionToken');
    sessionStorage.removeItem('userSession.customer');
    this.setUserStatus(null);
    //clearing loginResponse storage on logout. We should refine code to remove localStorage to clear all.
    // localStorage.clear()
    localStorage.removeItem('userSession.loginInfo');
    localStorage.removeItem('userSession.mfa');
    localStorage.removeItem('strongAuthenticationRequired');
    localStorage.removeItem('clientApp');
    localStorage.removeItem('lastIPAddress');
    localStorage.removeItem('customerID');
    localStorage.removeItem('reconfirmPersonalDetails');
    localStorage.removeItem('userSession.MFAApproved');

    this.setSessionToken(null);

    setTimeout(() => {
      this.setCustomer(null);
    }, 1000);
  }

  public isAuthenticated(): boolean {
    return !!this.getSessionToken();
  }

  public setCustomer(customer) {
    // store json data to keep storage space minimal
    if (customer) {
      localStorage.setItem('userSession.customer', JSON.stringify(customer));
      sessionStorage.setItem('userSession.customer', JSON.stringify(customer));
      this.customer = new CustomerModel(customer);
    } else {
      localStorage.removeItem('userSession.customer');
      sessionStorage.removeItem('userSession.customer');
      this.customer = customer;
    }
    this.customerSubject.next(this.customer);
  }

  public getCustomer() {
    return this.customerSubject;
  }

  public getUserStatus() {
    return this.userStatus;
  }

  public setUserStatus(status: TUserStatus) {
    if (status && status.sessionToken) {
      this.setSessionToken(status.sessionToken);
    }
    this.userStatus.next(status);
  }

  public async getCustomerSubscription() {
    if (this.customer) {
      setTimeout(() => {
        this.customerSubject.next(this.customer);
      }, 200);
    }
    const allowPreviousDisconnect = this.config.getConfig().FEATURE_ALLOW_PREVIOUS_DISCONNECTION || true;
    const customer: CustomerModel = (this.platform.is('capacitor') && allowPreviousDisconnect) ?
      JSON.parse(sessionStorage.getItem('userSession.customer')) :
      JSON.parse(localStorage.getItem('userSession.customer'));
    if (customer) {
      setTimeout(() => {
        this.setCustomer(customer);
      }, 200);
    }

    return this.customerSubject;
  }

  public unsubscribe() {
    this.customerSubject.complete();
  }

  public setCustomerId(customerId: number | string | null) {
    this.customerId = customerId;
    this.customerIdSubject.next(customerId);
  }

  public getCustomerId() {
    return this.customerId
  }

  setMFAStatus(status: eMfaStatuses): void {
    localStorage.setItem('userSession.mfa', status === eMfaStatuses.required ? 'true' : 'false');
    this._mfaStatus$.next(status);
  }

  setMFAApproval(status: string) {
    localStorage.setItem('userSession.MFAApproved', status);
  }

  getMFAApproval() {
    let mfa = localStorage.getItem('userSession.MFAApproved');
    return mfa;
  }

  isAuthenticatedWithMFA$(): Observable<boolean> {
    // need to unsubscribe when subscribe it
    return this._mfaStatus$.pipe(
      map(mfaStatus => !!this.sessionToken && mfaStatus != eMfaStatuses.required),
      filter(auth => !!auth)
    );
  }

  setLoginResponseForMFA() {
    this.mfaEnabled = true;
  }

  getLoginResponseForMFA() {
    return this.mfaEnabled;
  }
}
